// Panel "CORREGIR VOZ" de un video generado — ADR-2026-09-21-corregir-voz-es-una-creacion-nueva-gratis.
//
// El usuario ve la transcripción del video agrupada por oraciones, toca una palabra y
// escribe cómo tenía que decir; opcionalmente cambia un poco la velocidad. Al aplicar, el
// backend clona la voz del propio video, regenera la ORACIÓN entera (una palabra suelta
// no tiene la prosodia de la frase) y la pega en su lugar. El resultado es un video NUEVO
// en el historial: la sheet lo entrega por `onListo` y la superficie decide qué hacer.
// Gratis: no hay chequeo de saldo. Sin lip-sync: la boca de la palabra mal dicha suele
// ser la misma que la de la bien dicha.
//
// Mismo esqueleto que "Expansión mágica" (panel derecho en desktop, sheet abajo en
// mobile): es una herramienta más del mismo menú.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { AudioLines, Info, Loader2, RotateCcw, X } from "lucide-react";
import { atlasBackendFetch, pollJobUntilSettled } from "../lib/atlasBackend";
import { cargarVideoGenerado, firmarGenerada } from "../lib/videoGenerado";
import {
  agruparEnOraciones,
  edicionesDesde,
  esHablada,
  normalizarTranscript,
  type PalabraLab,
} from "./corregirVoz/transcript";
import { mensajeDeErrorDelBackend } from "./corregirVoz/mensajeDeError";

/** Lo mínimo que la sheet necesita del video de origen. */
export interface VideoACorregir {
  id: string;
  fileName: string;
}

/** La creación nueva, con la forma que las grillas ya entienden. */
export interface VideoCorregido {
  id: string;
  fileName: string;
  signedUrl: string;
  optSignedUrl?: string;
  videoUrl: string;
  isVideo: true;
  created_at?: string;
  variante_id?: string | null;
  variantes_ids?: string[] | null;
  datos_generacion?: Record<string, unknown> | null;
}

interface CorregirVozSheetProps {
  abierto: boolean;
  video: VideoACorregir | null;
  companyId: string;
  onCerrar: () => void;
  onListo: (nuevo: VideoCorregido) => void;
}

type Estado = "transcribiendo" | "lista" | "aplicando" | "error";

const VELOCIDAD_MIN = 0.8;
const VELOCIDAD_MAX = 1.25;

/** POST que puede responder 202 → se pollea el job. Devuelve el output final o lanza. */
async function pedirYEsperar(path: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
  const res = await atlasBackendFetch(path, body);
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (res.status === 202) {
    const jobId = json.job_id as string | undefined;
    if (!jobId) throw new Error("El servidor no devolvió el trabajo.");
    const settled = await pollJobUntilSettled(jobId, { timeoutMs: 10 * 60 * 1000, intervalMs: 3000 });
    if (!settled) throw new Error("Tardó demasiado. Probá de nuevo en un rato.");
    if (settled.status === "failed") throw new Error(settled.userMessage || "No pudimos procesar el video.");
    return settled.output ?? {};
  }
  if (!res.ok) throw new Error(mensajeDeErrorDelBackend(res.status, json));
  return json;
}

export default function CorregirVozSheet({ abierto, video, companyId, onCerrar, onListo }: CorregirVozSheetProps) {
  const [animado, setAnimado] = useState(false);
  const [estado, setEstado] = useState<Estado>("transcribiendo");
  const [error, setError] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [palabras, setPalabras] = useState<PalabraLab[] | null>(null);
  const [textos, setTextos] = useState<Record<number, string>>({});
  const [editando, setEditando] = useState<number | null>(null);
  const [velocidad, setVelocidad] = useState(1);
  const [intento, setIntento] = useState(0);

  const oraciones = useMemo(() => (palabras ? agruparEnOraciones(palabras) : []), [palabras]);
  const ediciones = useMemo(() => (palabras ? edicionesDesde(palabras, textos) : []), [palabras, textos]);
  const cambiaVelocidad = Math.abs(velocidad - 1) > 0.001;
  const hayAlgo = ediciones.length > 0 || cambiaVelocidad;
  const procesando = estado === "aplicando";

  // Apertura: animación + estado limpio.
  useEffect(() => {
    if (!abierto) {
      setAnimado(false);
      return;
    }
    setTextos({});
    setEditando(null);
    setVelocidad(1);
    setError(null);
    setPalabras(null);
    setVideoUrl(null);
    setEstado("transcribiendo");
    const id = requestAnimationFrame(() => setAnimado(true));
    return () => cancelAnimationFrame(id);
  }, [abierto, video?.id]);

  // Transcripción: se firma el mp4 y se le pide a Scribe. Se repite si el usuario reintenta.
  // Depende del id y del nombre, NO del objeto `video`: los padres lo arman inline en el
  // JSX y cada re-render traería una transcripción nueva.
  const videoId = video?.id;
  const videoFileName = video?.fileName;
  useEffect(() => {
    if (!abierto || !videoId || !videoFileName) return;
    let cancelado = false;
    (async () => {
      try {
        const url = await firmarGenerada(`${companyId}/full/${videoFileName}`);
        if (!url) throw new Error("No pudimos abrir el video.");
        if (cancelado) return;
        setVideoUrl(url);
        const out = await pedirYEsperar("/v1/media/transcript", { url });
        const p = normalizarTranscript(out);
        if (!p) throw new Error("No pudimos transcribir el video.");
        if (cancelado) return;
        if (!p.some(esHablada)) throw new Error("No encontramos voz en este video.");
        setPalabras(p);
        setEstado("lista");
      } catch (err) {
        if (cancelado) return;
        setError(err instanceof Error ? err.message : String(err));
        setEstado("error");
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [abierto, videoId, videoFileName, companyId, intento]);

  const cerrar = () => {
    if (procesando) return;
    setAnimado(false);
    setTimeout(onCerrar, 300);
  };

  const aplicar = async () => {
    if (!video || !palabras || !hayAlgo || procesando) return;
    setError(null);
    setEstado("aplicando");
    try {
      const out = await pedirYEsperar("/v1/videos/corregir-voz", {
        video_id: video.id,
        palabras,
        ediciones,
        velocidad,
      });
      const videoId = out.video_id as string | undefined;
      if (!videoId) throw new Error("El video se corrigió pero no pudimos cargarlo. Mirá el historial.");
      // La lectura + firma vive en lib/videoGenerado (la comparte "Pasar a 1080p"); lo que se
      // emite es campo por campo lo mismo que antes.
      const cargado = await cargarVideoGenerado(videoId, companyId);
      if (!cargado) throw new Error("El video se corrigió pero no pudimos cargarlo. Mirá el historial.");
      setAnimado(false);
      onListo(cargado);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setEstado("lista");
    }
  };

  if (!abierto || !video) return null;

  const transcript =
    estado === "transcribiendo" ? (
      <div className="flex items-center gap-2 py-6 justify-center text-[13px] text-brand-black/60" role="status">
        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
        Leyendo lo que dice el video…
      </div>
    ) : estado === "error" && !palabras ? (
      <div className="py-4 text-center">
        <p className="text-[13px] text-red-600 mb-3">{error}</p>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setEstado("transcribiendo");
            setIntento((n) => n + 1);
          }}
          className="py-2 px-4 rounded-lg border border-neutral-200 text-[13px] text-brand-black hover:bg-neutral-50"
        >
          Reintentar
        </button>
      </div>
    ) : palabras ? (
      <div className="space-y-2">
        {oraciones.map((indices, o) => {
          const tocada = indices.some((i) => ediciones.some((e) => e.indice === i));
          return (
            <div
              key={o}
              className={`flex flex-wrap gap-1 p-2.5 rounded-lg border transition-colors ${
                tocada ? "border-[rgb(var(--brand-primary-rgb)_/_0.4)] bg-[rgb(var(--brand-primary-rgb)_/_0.07)]" : "border-neutral-200 bg-white"
              }`}
            >
              {indices.map((i) => {
                const p = palabras[i];
                const nuevo = textos[i]?.trim();
                const cambiado = !!nuevo && nuevo !== p.text.trim();
                if (editando === i) {
                  return (
                    <input
                      key={i}
                      autoFocus
                      defaultValue={textos[i] ?? p.text}
                      disabled={procesando}
                      onBlur={(e) => {
                        setTextos((t) => ({ ...t, [i]: e.target.value }));
                        setEditando(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                        if (e.key === "Escape") setEditando(null);
                      }}
                      className="py-0.5 px-1.5 rounded-md border border-brand-primary text-[14px] w-28 outline-none"
                    />
                  );
                }
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={procesando}
                    onClick={() => setEditando(i)}
                    title={cambiado ? `Decía: ${p.text}` : "Tocá para corregir"}
                    className={`py-0.5 px-1.5 rounded-md text-[14px] leading-snug transition-colors ${
                      cambiado
                        ? "bg-brand-primary text-white"
                        : "text-brand-black hover:bg-neutral-100"
                    }`}
                  >
                    {cambiado ? nuevo : p.text}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    ) : null;

  const controles = palabras ? (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide">Velocidad</p>
        <span className="text-[13px] tabular-nums text-brand-black/80">{velocidad.toFixed(2)}x</span>
      </div>
      <input
        type="range"
        min={VELOCIDAD_MIN}
        max={VELOCIDAD_MAX}
        step={0.05}
        value={velocidad}
        disabled={procesando}
        onChange={(e) => setVelocidad(Number(e.target.value))}
        className="w-full accent-brand-primary"
      />
      <p className="text-[13px] text-[#3C3C43]/60 mt-1">
        {cambiaVelocidad ? "Video y voz van juntos: la boca sigue en sincronía." : "Sin cambio de velocidad."}
      </p>
    </div>
  ) : null;

  const progreso = procesando ? (
    <div className="mt-4" role="status" aria-live="polite">
      <div className="flex items-center gap-2 mb-2">
        <Loader2 className="w-4 h-4 animate-spin text-brand-primary" />
        <span className="text-[13px] text-brand-black/80">
          {ediciones.length > 0 ? "Regenerando la voz…" : "Ajustando la velocidad…"}
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-neutral-200">
        <div
          className="h-full rounded-full"
          style={{
            width: "100%",
            transformOrigin: "left",
            animation: "expansion-progreso 60s cubic-bezier(0, 0, 0.2, 1) forwards",
            background: "linear-gradient(to right, #D1D5DB 10%, var(--brand-primary) 70%)",
          }}
        />
      </div>
    </div>
  ) : null;

  const info = (
    <div className="mt-5">
      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">Información</p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        <div className="px-4 py-[11px]">
          <div className="flex items-center gap-3 mb-2">
            <AudioLines className="w-5 h-5 text-brand-primary shrink-0" />
            <span className="text-[15px] text-brand-black/90">Se guarda como video nuevo</span>
          </div>
          <p className="text-[13px] text-[#3C3C43]/60">
            Se vuelve a decir la oración completa con la misma voz. El original queda intacto en tu historial.
          </p>
        </div>
        <div className="ml-[46px] h-px bg-black/[0.08]" />
        <div className="px-4 py-[11px]">
          <div className="flex items-center gap-2">
            <Info className="w-3.5 h-3.5 text-brand-primary shrink-0" />
            <span className="text-[13px] text-[#3C3C43]/60">No consume créditos</span>
          </div>
        </div>
      </div>
    </div>
  );

  const cuerpo = (
    <>
      {videoUrl && (
        <div className="flex justify-center mb-4">
          <video src={videoUrl} controls playsInline className="max-h-[220px] rounded-[10px] bg-black" />
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide">
          Tocá una palabra para corregirla
        </p>
        {ediciones.length > 0 && !procesando && (
          <button
            type="button"
            onClick={() => setTextos({})}
            className="inline-flex items-center gap-1 text-[12px] text-[#3C3C43]/60 hover:text-brand-black"
          >
            <RotateCcw className="w-3 h-3" /> Deshacer
          </button>
        )}
      </div>
      {transcript}
      {controles}
      {progreso}
      {error && palabras && <p className="mt-3 text-[13px] text-red-600">{error}</p>}
      {info}
    </>
  );

  const pie = (
    <>
      <button
        type="button"
        onClick={cerrar}
        disabled={procesando}
        className="py-2.5 px-5 rounded-lg text-[15px] text-brand-black/70 hover:bg-neutral-100 transition-colors disabled:opacity-50"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={aplicar}
        disabled={!hayAlgo || procesando || estado !== "lista"}
        className="py-2.5 px-5 rounded-lg text-[15px] font-medium bg-brand-primary text-white hover:bg-brand-primary-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
      >
        {procesando && <Loader2 className="w-4 h-4 animate-spin" />}
        {procesando ? "Corrigiendo…" : "Corregir voz"}
      </button>
    </>
  );

  // Nada de lo que pasa adentro del panel llega al visor de atrás: ni las flechas con las
  // que el usuario mueve el cursor en la palabra (el lightbox del historial las escucha en
  // `window` y cambiaba de video), ni Escape, ni los clics.
  const aislar = {
    onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
    onClick: (e: React.MouseEvent) => e.stopPropagation(),
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 z-[200] bg-black/40 transition-opacity duration-300 ${animado ? "opacity-100" : "opacity-0"}`}
        onClick={(e) => {
          e.stopPropagation();
          if (!procesando) cerrar();
        }}
      />

      {/* Desktop: panel lateral */}
      <div
        {...aislar}
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[201] flex-col overflow-hidden transition-transform duration-300 ease-out ${animado ? "translate-x-0" : "translate-x-[calc(100%+32px)]"}`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-[17px] font-semibold text-brand-black/90">Corregir voz</h3>
          <button
            type="button"
            onClick={cerrar}
            disabled={procesando}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className="flex-1 overflow-y-auto px-5 py-4">{cuerpo}</div>
        <div className="px-5 py-3 flex justify-end gap-3">{pie}</div>
      </div>

      {/* Mobile: sheet desde abajo */}
      <div
        {...aislar}
        className={`sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-white rounded-t-[20px] shadow-[0_-8px_40px_rgba(0,0,0,0.12)] flex flex-col max-h-[92vh] transition-transform duration-300 ease-out ${animado ? "translate-y-0" : "translate-y-full"}`}
      >
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-10 h-1 rounded-full bg-neutral-300" />
        </div>
        <div className="flex items-center justify-between px-5 pb-3">
          <h3 className="text-[17px] font-semibold text-brand-black/90">Corregir voz</h3>
          <button
            type="button"
            onClick={cerrar}
            disabled={procesando}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4">{cuerpo}</div>
        <div className="px-5 py-3 pb-[max(12px,env(safe-area-inset-bottom))] flex justify-end gap-3 border-t border-black/[0.06]">
          {pie}
        </div>
      </div>
    </>,
    document.body,
  );
}
